import React from 'react'
import { useNavigate } from 'react-router-dom'
import RecentDesigns from '../../components/Landing/RecentDesigns'
import DesignCountChart from '../../components/Dashboard/DesignCountChart'
import TodayTomorrowDesigns from '../../components/Dashboard/TodayTomorrowDesigns'
import DashboardCalendar from '../../components/Dashboard/DashboardCalendar'
import { useAuth } from '../../contexts/AuthContext'

const DashboardHomePage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = user?.role === 'ADMIN'

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Welcome Header */}
      <div className="mb-8">
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl p-8 text-white relative overflow-hidden">
          <div className="relative z-10">
            <h1 className="text-4xl font-bold mb-4">
              What will you design today?
            </h1>
            <p className="text-blue-100 text-lg mb-6">
              Create stunning videos, images, and presentations with our powerful editor
            </p>
            
            {/* Quick Action Tabs */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => navigate('/dashboard/templates')}
                className="px-4 py-2 rounded-lg font-medium transition-all duration-200 bg-white/20 text-white hover:bg-white/30"
              >
                Templates
              </button>
              <button
                onClick={() => navigate('/dashboard/photos')}
                className="px-4 py-2 rounded-lg font-medium transition-all duration-200 bg-white/20 text-white hover:bg-white/30"
              >
                Photos
              </button>
              <button
                onClick={() => navigate('/dashboard/graphics')}
                className="px-4 py-2 rounded-lg font-medium transition-all duration-200 bg-white/20 text-white hover:bg-white/30"
              >
                Graphics
              </button>
              <button
                onClick={() => navigate('/dashboard/audio')}
                className="px-4 py-2 rounded-lg font-medium transition-all duration-200 bg-white/20 text-white hover:bg-white/30"
              >
                Audio
              </button>
              <button
                onClick={() => navigate('/dashboard/videos')}
                className="px-4 py-2 rounded-lg font-medium transition-all duration-200 bg-white/20 text-white hover:bg-white/30"
              >
                Videos
              </button>
              {isAdmin && (
                <>
                  <button
                    onClick={() => navigate('/dashboard/manage-templates')}
                    className="px-4 py-2 rounded-lg font-medium transition-all duration-200 bg-white/20 text-white hover:bg-white/30"
                  >
                    🛠️ Manage Templates
                  </button>
                  <button
                    onClick={() => navigate('/dashboard/categories')}
                    className="px-4 py-2 rounded-lg font-medium transition-all duration-200 bg-white/20 text-white hover:bg-white/30"
                  >
                    📁 Manage Categories
                  </button>
                  <button
                    onClick={() => navigate('/dashboard/tenants')}
                    className="px-4 py-2 rounded-lg font-medium transition-all duration-200 bg-white/20 text-white hover:bg-white/30"
                  >
                    🏢 Manage Tenants
                  </button>
                </>
              )}
            </div>
          </div>
          
          {/* Background Pattern */}
          <div className="absolute top-0 right-0 w-64 h-64 opacity-10">
            <svg viewBox="0 0 200 200" className="w-full h-full">
              <defs>
                <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="1"/>
                </pattern>
              </defs>
              <rect width="200" height="200" fill="url(#grid)" />
            </svg>
          </div>
        </div>
      </div>

      <div className="space-y-8">
        {/* Dashboard Analytics Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Design Count Chart */}
          <div className="lg:col-span-2">
            <DesignCountChart />
          </div>
          
          {/* Today/Tomorrow Designs Table */}
          <div className="lg:col-span-1">
            <TodayTomorrowDesigns />
          </div>
          
          {/* Calendar View */}
          <div className="lg:col-span-1">
            <DashboardCalendar />
          </div>
        </div>

        {/* Recent Designs */}
        <RecentDesigns />
      </div>

      {/* Quick Actions */}
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Create Video</h3>
          <p className="text-gray-600 mb-4">Edit and create stunning videos with our powerful video editor</p>
          <button className="text-blue-600 font-medium hover:text-blue-700">
            Start creating →
          </button>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Edit Images</h3>
          <p className="text-gray-600 mb-4">Professional image editing tools for all your creative needs</p>
          <button className="text-green-600 font-medium hover:text-green-700">
            Start editing →
          </button>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
              <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a2 2 0 002 2h8a2 2 0 002-2V3a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zM6 7a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Presentations</h3>
          <p className="text-gray-600 mb-4">Create engaging presentations that captivate your audience</p>
          <button className="text-purple-600 font-medium hover:text-purple-700">
            Get started →
          </button>
        </div>
      </div>
    </div>
  )
}

export default DashboardHomePage
